import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Button, Chip, TextField, MenuItem, CircularProgress, Typography,
} from '@mui/material';
import { DataGrid } from '../../../components/common/datagrid/ResponsiveDataGrid';
import AddIcon from '@mui/icons-material/Add';
import { useSnackbar } from 'notistack';
import * as groupApi from '../../../lib/api/cstGroup';
import GroupFormDialog from '../../../components/chancemng/group/GroupFormDialog';
import { getNumberComma } from '../../../utils/numberUtils';

const GroupListContainer = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [keyword, setKeyword] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await groupApi.getGroups({ searchStatus: status, searchKeyword: keyword });
      const list = res.data?.list || [];
      setRows(list.map((r) => ({ id: r.groupUuid, ...r })));
    } catch {
      enqueueSnackbar('묶음 목록을 불러오지 못했습니다.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [status, keyword, enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  const money = (v) => (v ? getNumberComma(v) : '-');

  const columns = [
    { field: 'groupNm', headerName: '이름', flex: 1.4, minWidth: 180 },
    {
      field: 'groupTypNm', headerName: '종류', width: 90,
      renderCell: ({ row }) => (
        <Chip size="small" variant="outlined" label={row.groupTypNm || row.groupTyp} />
      ),
    },
    {
      field: 'period', headerName: '기간', width: 170, sortable: false,
      renderCell: ({ row }) => (row.stDt || row.edDt
        ? <span style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
            {row.stDt || '…'} ~ {row.edDt || '…'}
          </span>
        : '-'),
    },
    { field: 'ownerNm', headerName: '담당', width: 90,
      renderCell: ({ value }) => value || '-' },
    { field: 'chanceCnt', headerName: '기회', width: 70, align: 'right', headerAlign: 'right' },
    { field: 'expSales', headerName: '예상 매출', width: 130, align: 'right', headerAlign: 'right',
      renderCell: ({ value }) => money(value) },
    { field: 'expBuy', headerName: '예상 매입', width: 130, align: 'right', headerAlign: 'right',
      renderCell: ({ value }) => money(value) },
    {
      field: 'delayCnt', headerName: '지연', width: 80, align: 'center', headerAlign: 'center',
      renderCell: ({ value }) => (value > 0
        ? <Chip size="small" color="warning" label={`${value}건`} />
        : <Typography variant="caption" color="text.secondary">—</Typography>),
    },
    {
      field: 'groupStatus', headerName: '상태', width: 80,
      renderCell: ({ value }) => (
        <Chip size="small" label={value === 'CLOSED' ? '종료' : '진행'}
              color={value === 'CLOSED' ? 'default' : 'success'} />
      ),
    },
  ];

  return (
    <Box sx={{ m: 1 }}>
      <Paper variant="outlined" sx={{ p: 1.5, mb: 2, display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          select size="small" label="상태" value={status}
          onChange={(e) => setStatus(e.target.value)} sx={{ minWidth: 120 }}
        >
          <MenuItem value="">전체</MenuItem>
          <MenuItem value="OPEN">진행</MenuItem>
          <MenuItem value="CLOSED">종료</MenuItem>
        </TextField>
        <TextField
          size="small" label="이름 검색" value={keyword}
          onChange={(e) => setKeyword(e.target.value)} sx={{ minWidth: 200 }}
        />
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="contained" size="small" startIcon={<AddIcon />}
                onClick={() => setDialogOpen(true)}>
          묶음 추가
        </Button>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : (
        <DataGrid
          rows={rows}
          columns={columns}
          autoHeight
          disableColumnMenu
          density="compact"
          pageSizeOptions={[20, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 20 } } }}
          onRowClick={(p) => navigate(`/chancemng/groups/${p.id}`)}
          sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
          localeText={{ noRowsLabel: '아직 만든 묶음이 없습니다. 오른쪽 위 "묶음 추가"로 시작하세요.' }}
        />
      )}

      <GroupFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSaved={() => load()}
      />
    </Box>
  );
};

export default GroupListContainer;
